import { Badge } from 'src/design/atoms/Badge';
import { resolveStepColor } from 'src/app/workflows/workflowStatusColumn';
import { LoadingOverlay } from 'src/design/atoms/LoadingOverlay';
import { Paper } from 'src/design/atoms/Paper';
import { Title } from 'src/design/atoms/Title';
import { Table } from 'src/design/molecules/Table';
import { groupBy, sumBy } from 'lodash';
import { MRT_ColumnDef } from 'mantine-react-table';
import React, { useMemo } from 'react';
import { RfiDto } from 'src/api/FiretrolApiClient';
import { useRfisList } from 'src/api/queries/rfis/useRfiQueries';
import { useJobs } from 'src/hooks/useJobs';
import { BasicButton } from 'src/design/atoms/BasicButton';
import { LinkButton } from 'src/design/atoms/LinkButton';
import { Currency } from 'src/design/atoms/Currency';
import { Modal } from 'src/design/molecules/Modal';
import {
  useTable,
  useTableState,
} from 'src/design/organisms/DataTable/useTable';
import { useTableExports } from 'src/design/organisms/useTableExports';

interface RFISummaryProps {
  jobId: number;
}

type RfiWithNumericCost = RfiDto & { numCost: number; numSchedule: number };

const getRFIColumns = (): MRT_ColumnDef<RfiWithNumericCost>[] => [
  {
    accessorKey: 'rfiNumber',
    header: 'RFI Number',
    size: 175,
    Cell: ({ cell }) => {
      const rfi = cell.row.original;
      return (
        <LinkButton to={`/jobs/${rfi.jobId}/rfis/${rfi.id}`}>
          {rfi.rfiNumber ?? ''}
        </LinkButton>
      );
    },
  },
  {
    accessorKey: 'status',
    header: 'Status',
    size: 200,
    Cell: ({ cell }) => {
      const rfi = cell.row.original;
      const stepColor = resolveStepColor(rfi);
      return (
        <Badge
          color={stepColor || 'gray'}
          variant={stepColor ? 'filled' : 'light'}
        >
          {(rfi.status ?? '').toUpperCase()}
        </Badge>
      );
    },
  },
  {
    accessorKey: 'impact',
    accessorFn: (rfi) => (rfi.impact ?? '').toUpperCase(),
    header: 'Impact',
    size: 200,
  },
  {
    accessorKey: 'fromUser.fullName',
    header: 'From',
    size: 200,
  },
  {
    accessorKey: 'reporter.fullName',
    header: 'Reporter',
    size: 200,
  },
];

export const RFISummary: React.FC<RFISummaryProps> = ({ jobId }) => {
  const { selectedJob } = useJobs();
  const { data, isLoading: loading } = useRfisList(jobId);
  const [selectedRfis, setSelectedRfis] = React.useState<RfiWithNumericCost[]>(
    [],
  );
  const [openDialog, setOpenDialog] = React.useState(false);

  const allRfis = React.useMemo<RfiWithNumericCost[]>(
    () =>
      (data ?? []).map((rfi) => ({
        ...rfi,
        numCost: Number(rfi.costDollars?.replaceAll(',', '') || 0),
        numSchedule: Number(rfi.costDays || 0),
      })),
    [data],
  );
  const rfis = React.useMemo(
    () => groupBy(allRfis, (c) => c.status ?? 'Unknown'),
    [allRfis],
  );

  const columns = useMemo(() => getRFIColumns(), []);

  const tableState = useTableState({
    identifier: 'rfi-summary',
    columns: columns,
    defaultSorting: [{ id: 'rfiNumber', desc: false }],
  });

  const tableExports = useTableExports<RfiWithNumericCost>('rfis', (data) => {
    return data.map((item) => ({
      ['Job Name']: selectedJob?.name,
      ['Job Number']: selectedJob?.jobNumber,
      ['RFI Number']: item.rfiNumber,
      ['Status']: item.status,
      ['Cost']: Number(item.costDollars) || 0,
      ['Schedule']: Number(item.costDays) || 0,
      ['Reporter']: item.reporter?.fullName || '',
    }));
  });

  const { table } = useTable({
    ...tableState,
    tableOptions: {
      enableRowActions: false,
      enableClickToCopy: false,
      columns: columns,
      data: selectedRfis,
      manualPagination: false,
      manualSorting: false,
      manualFiltering: false,
    },
    getRowId: (row) => String(row.id),
    totalCount: selectedRfis.length,
    isLoading: false,
    isFetching: false,
    isError: false,
    ...tableExports,
  });

  const showList = (items: RfiWithNumericCost[]) => {
    setSelectedRfis(items);
    setOpenDialog(true);
  };

  const hideList = () => {
    setSelectedRfis([]);
    setOpenDialog(false);
  };

  const renderLines = () => {
    return Object.keys(rfis).map((key) => {
      const items = rfis[key];
      const totalCost = sumBy(items, (c) => c.numCost);
      const totalSchedule = sumBy(items, (c) => c.numSchedule);
      return (
        <Table.Tr key={key}>
          <Table.Td>{key}</Table.Td>
          <Table.Td>
            <BasicButton
              size="compact-xs"
              variant="subtle"
              onClick={() => showList(items)}
            >
              {items.length}
            </BasicButton>
          </Table.Td>
          <Table.Td>{<Currency value={totalCost} />}</Table.Td>
          <Table.Td>{totalSchedule} days</Table.Td>
        </Table.Tr>
      );
    });
  };

  const renderAllLines = () => {
    const totalCost = sumBy(allRfis, (c) => c.numCost);
    const totalSchedule = sumBy(allRfis, (c) => c.numSchedule);
    return (
      <Table.Tr>
        <Table.Td>All RFIs</Table.Td>
        <Table.Td>
          <BasicButton
            size="compact-xs"
            variant="subtle"
            onClick={() => showList(allRfis)}
          >
            {allRfis.length}
          </BasicButton>
        </Table.Td>
        <Table.Td>{<Currency value={totalCost} />}</Table.Td>
        <Table.Td>{totalSchedule} days</Table.Td>
      </Table.Tr>
    );
  };

  return (
    <Paper pos="relative">
      <LoadingOverlay visible={loading} />
      <Title order={5} mb="md">
        RFIs
      </Title>

      {!loading && (
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Status</Table.Th>
              <Table.Th>Count</Table.Th>
              <Table.Th>Cost</Table.Th>
              <Table.Th>Schedule</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {renderAllLines()}
            {renderLines()}
          </Table.Tbody>
        </Table>
      )}

      <Modal opened={openDialog} onClose={hideList} title="RFIs" size="80%">
        {table}
      </Modal>
    </Paper>
  );
};
